/**
 * Reading a `prices:{symbol}` push. Task 33.
 *
 * Its own module with no React and no charting import, for the same reason `charts/gaps.ts` is:
 * this is the part with a decision in it, so it can be unit-tested rather than eyeballed on a
 * canvas.
 *
 * ## The decision is what to reject
 *
 * A push arrives as `Record<string, unknown>` — it crossed a network as JSON, and the socket
 * frames are the one part of this API's contract the generated client cannot check, because
 * WebSocket routes do not appear in the OpenAPI document. So the shape is validated here, in
 * one place, rather than trusted at each call site.
 *
 * **Every price must arrive as a string** (H6). A number here is not a value to coerce, it is
 * evidence that something on the server serialised a `Decimal` as a JSON number — and by the
 * time this code sees it the trailing digits are already gone, so `Number(...)` would launder a
 * defect into a plausible price. `null` is returned instead and the push is dropped, which
 * presents as a chart that stops updating: visible, and the correct direction to fail in.
 *
 * `jmt_api/topics.py` is the other end of this contract.
 */

import type { BarResponse } from '../api/generated';

/** The topic one instrument's prices arrive on. Mirrors `jmt_api.topics.price_topic`. */
export function priceTopic(symbol: string): string {
  return `prices:${symbol}`;
}

function decimalString(value: unknown): string | null {
  // Rejected rather than coerced — see the module docstring. `typeof` and not a regex: the
  // question is whether the *server* sent a string, not whether the digits look plausible.
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * A push payload as a `BarResponse`, or `null` when it is not one.
 *
 * `timeframe` is returned rather than filtered here: the topic is keyed on symbol, so pushes
 * for every rung arrive on it, and *which* rung the screen wants is the screen's business. This
 * function's job is to say whether the frame is a bar at all.
 */
export function barFromPush(
  data: Record<string, unknown>,
): { bar: BarResponse; timeframe: string; symbol: string } | null {
  const ts = typeof data.ts === 'string' ? data.ts : null;
  const timeframe = typeof data.timeframe === 'string' ? data.timeframe : null;
  const symbol = typeof data.symbol === 'string' ? data.symbol : null;
  const open = decimalString(data.open);
  const high = decimalString(data.high);
  const low = decimalString(data.low);
  const close = decimalString(data.close);
  const volume = decimalString(data.volume);

  if (!ts || !timeframe || !symbol || !open || !high || !low || !close || !volume) return null;

  return {
    symbol,
    timeframe,
    bar: {
      ts,
      open,
      high,
      low,
      close,
      volume,
      // The field is on the wire by construction (`topics.price_payload`), so a non-boolean
      // means an older publisher rather than a lie — and `false` is the safe default because
      // a *manufactured* bar arriving as `true` is the case that must never be lost, not the
      // other way round.
      synthetic: typeof data.synthetic === 'boolean' ? data.synthetic : false,
      // `null`, not omitted. `BarResponse` declares both as nullable and `bar.closed` carries
      // neither — the publisher's payload is the vault's documented one and these are not in
      // it. A live candle therefore has no quote volume, which nothing on the chart reads.
      quote_volume: null,
      trades: null,
    },
  };
}
